// State for the two price-change only screens: plan selection and price confirmation.
// Only populated when the exchange type is ExchangeTypes.PriceChange.
//
// Every currency value here is MINOR currency (pence/cents), held as a whole number. Sources that
// return major currency are converted in PriceChangePlan before they reach this reducer, and the
// screens convert back to major for display. Working in minor keeps the arithmetic exact and
// matches what the backend models expect on the wire.

export type PriceChangeState = {
  // The Flexi plan the customer is moving on to
  newPlanId: string;
  newPlanName: string;
  // Weekly hire fee of the selected plan, before any coupon discount
  newPlanWeeklyFee: number | null;  // minor currency

  // Dynamic pricing coupon. Empty when the operator clears it, meaning no discount.
  couponId: string;
  couponDiscount: number | null;  // minor currency

  // Read from Zoho, null until the subscription has loaded.
  // oldWeeklyFee (Rental_Fee) is already net of any discount, so it is the before/after comparison
  // figure. oldWeeklyDiscount (Total_Weekly_Discount) is only used to find the customer's existing
  // coupon, since Zoho does not store the coupon id.
  oldWeeklyFee: number | null;  // minor currency
  oldWeeklyDiscount: number | null;  // minor currency

  // Set on the confirmation screen once the operator explicitly confirms the new price
  priceConfirmed: boolean;
};

export type PriceChangeAction =
  | { type: "USER_SELECTS_PLAN"; payload: { planId: string; planName: string; weeklyFee: number } }
  | { type: "USER_SELECTS_COUPON"; payload: { couponId: string; discount: number | null } }
  | { type: "SUBSCRIPTION_PRICING_LOADED"; payload: { oldWeeklyFee: number | null; oldWeeklyDiscount: number | null } }
  | { type: "USER_CONFIRMS_PRICE"; payload: boolean };

export const priceChangeInitialState: PriceChangeState = {
  newPlanId: "",
  newPlanName: "",
  newPlanWeeklyFee: null,
  couponId: "",
  couponDiscount: null,
  oldWeeklyFee: null,
  oldWeeklyDiscount: null,
  priceConfirmed: false,
};

// The fee the customer will actually pay each week, once the coupon is taken off the plan fee.
// Both operands are whole minor-currency amounts, so the subtraction is exact - no rounding needed.
export function calculateNewWeeklyFee(state: PriceChangeState): number | null {
  if (state.newPlanWeeklyFee == null) return null;
  return state.newPlanWeeklyFee - (state.couponDiscount ?? 0);
}

export function priceChangeReducer(state: PriceChangeState, action: PriceChangeAction): PriceChangeState {
  switch (action.type) {
    case "USER_SELECTS_PLAN":
      // Changing the plan changes the price, so a prior confirmation no longer applies
      return {
        ...state,
        newPlanId: action.payload.planId,
        newPlanName: action.payload.planName,
        newPlanWeeklyFee: action.payload.weeklyFee,
        priceConfirmed: false,
      };
    case "USER_SELECTS_COUPON":
      return {
        ...state,
        couponId: action.payload.couponId,
        couponDiscount: action.payload.discount,
        priceConfirmed: false,
      };
    case "SUBSCRIPTION_PRICING_LOADED":
      return {
        ...state,
        oldWeeklyFee: action.payload.oldWeeklyFee,
        oldWeeklyDiscount: action.payload.oldWeeklyDiscount,
      };
    case "USER_CONFIRMS_PRICE":
      return { ...state, priceConfirmed: action.payload };
    default:
      console.log(`Error - incorrect action sent ${action} ${state}`);
      return state;
  }
}
